import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, FieldError, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useQuery } from "react-query";
import { TextField } from "@mui/material";
import DeleteBin2LineIcon from "remixicon-react/DeleteBin2LineIcon";
import { Badge, Button, Popin, Text, Button as UIButton } from "$components";
import FileUploader from "$components/FileUploader/FileUploader";
import JBadge from "$components/JBadge/JBadge";
import WarehouseSelector from "$components/JSelectors/WarehouseSelector/WarehouseSelector";
import ReceptionForm from "$containers/Receptions/ReceptionList/components/ReceptionForm";
import CField, { CFieldInputProps } from "$forms/CField";
import CompanySelect from "$forms/CompanySelect";
import GenericSelect from "$forms/GenericSelect";
import useAuth from "$hooks/useAuth";
import useJStyles from "$hooks/useJStyles";
import { useList } from "$hooks/useList";
import useOrder, { STATUS_ORDERS } from "$hooks/useOrder";
import useReception from "$hooks/useReception";
import {
  CompanyServices,
  DockyardServices,
  EDBService,
  EntrepotService,
  ProductService,
  ReceptionService,
  OrderService,
} from "$services";
import {
  areOrderRefsAlreadyUsed,
  getIsOrderInAnEDB,
} from "$services/Order.service";
import { appState } from "$store";
import { Company, CompanyType, OrderStatus } from "$types";
import { Services } from "$utils/typeConstants";
import { isAnObjectId, toLocalDateString } from "$utils/utils";

import BataillogDatePicker from "$components/BataillogDatePicker/BataillogDatePicker";
import OrderReceptions from "./OrderReceptions";
import SubordersForm from "./SubordersForm";
import { useCommonStyles } from "$containers/CommonStyles/CommonStyles";
import { queryClient } from "$utils/queryClient";
import { Loader } from "$components/Layout/Loader";
import { CacheInvalidationAction } from "$utils/cache";
import { parseStrapiFormat } from "$utils/strapi";

const buttonStyle = {
  minWidth: "64px",
  width: "24px",
  height: "24px",
  padding: "8px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};

const rightStyle = {
  flex: 1,
  rowGap: "8px",
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
};

const extractData = (dataObject) => {
  if (!dataObject) return [];
  if (Array.isArray(dataObject)) return dataObject;
  if (dataObject.data && Array.isArray(dataObject.data)) return dataObject.data;
  return [];
};

export interface IOrderFormProps {
  className?: string;
}

type Props = {
  onSubmit?: (data: any) => Promise<any>;
  onCancelOrder?: (data: any) => void;
  onReset?: () => void;
  order?: any;
  orderNewValue?: any;
  isSubmitting?: boolean;
  isTransfer?: boolean;
  setPopinOpen?: React.Dispatch<React.SetStateAction<boolean>>;
};

function OrderForm({
  onSubmit,
  onReset,
  onCancelOrder,
  order: initialOrder,
  orderNewValue,
  isSubmitting,
  // defaultCompanyFilter,
  isTransfer,
  setPopinOpen,
}: Props) {
  const { classes } = useOrder();
  const commonClasses = useCommonStyles();
  const { isAdmin, token } = useAuth();
  const { jclasses } = useJStyles();

  const { data: order } = useQuery(
    [CacheInvalidationAction.GET_ORDER, initialOrder?.id],
    () => OrderService.getOrder(initialOrder?.id),
    {
      enabled: !!initialOrder?.id,
      initialData: initialOrder,
    }
  );

  const [editedReception, setEditedReception] = useState(null);
  const [currentOPs, setCurrentOPs] = useState<any[] | null>(null);
  const [edbWhereOrderIsIncluded, setEDBWhereOrderIsIncluded] = useState([]);
  const [edbIdWhereOrderIsIncluded, setEDBIdWhereOrderIsIncluded] = useState(
    []
  );
  const isOrderCanceled = order && order.status === OrderStatus.CANCELED;
  const isReadOnly = !onSubmit || !isAdmin() || isOrderCanceled;
  const [companiesSupplier, setCompaniesSupplier] = useState([]);
  const [dockyardsLoaded, setDockyardsLoaded] = useState(false);
  const [productsLoaded, setProductsLoaded] = useState(false);

  useEffect(() => {
    const isOrderInAnEBD = async () => {
      const res = order ? await getIsOrderInAnEDB(order.id) : [];

      let edbList;
      if (res.length > 0) {
        const queryKeyEdbs = {
          $inId: [...new Set(res.map((i) => i.edbId))],
          company: order.company,
        };
        edbList = await queryClient.getQueryData([
          CacheInvalidationAction.GET_EDBS,
          queryKeyEdbs,
        ]);
        if (!edbList) {
          edbList = await queryClient.fetchQuery(
            [CacheInvalidationAction.GET_EDBS, queryKeyEdbs],
            () => EDBService.getEDBs(queryKeyEdbs),
            { cacheTime: 5 * 60 * 1000 }
          );
        }
      }

      setEDBWhereOrderIsIncluded(edbList?.data?.map((i) => i.edbId));
      setEDBIdWhereOrderIsIncluded(edbList?.data?.map((i) => i._id));
    };

    isOrderInAnEBD();
  }, [order]);

  const getCurrentOPs = useCallback(async () => {
    if (edbIdWhereOrderIsIncluded && edbIdWhereOrderIsIncluded.length > 0) {
      const receptionReferences = order?.receptions?.flatMap((reception: any) =>
        reception?.receptionReferences?.map(
          (receptionReference: any) => receptionReference.id
        )
      );

      const allOPResults = await Promise.all(
        edbIdWhereOrderIsIncluded.map(async (edbId) => {
          return EDBService.getOpsByEdbAndReceptionReferences(
            token,
            edbId,
            receptionReferences
          );
        })
      );
      setCurrentOPs(allOPResults.flat());
    } else {
      setCurrentOPs(null);
    }
  }, [edbIdWhereOrderIsIncluded, order?.receptions, token]);

  useEffect(() => {
    if (order?.id && order?.receptions) {
      getCurrentOPs();
    }
  }, [getCurrentOPs, order?.id, order?.receptions]);

  // Check if order can be deleted
  const canDeleteOrder = useMemo(() => {
    if (!order) return false;

    // Cannot delete if has receptions
    const hasReceptions = order?.receptions && order.receptions.length > 0;

    // Cannot delete if included in EDB
    const isInEDB =
      edbWhereOrderIsIncluded && edbWhereOrderIsIncluded.length > 0;

    return !hasReceptions && !isInEDB;
  }, [order, edbWhereOrderIsIncluded]);

  // Handle delete order
  const handleDeleteOrder = useCallback(async () => {
    if (order) {
      try {
        const result = await OrderService.deleteOrderItem(order.id);
        if (result && !result.error) {
          setPopinOpen(false);
        } else {
          console.error("Error deleting order:", result?.error);
        }
      } catch (error) {
        console.error("Error deleting order:", error);
      }
    }
  }, [order, setPopinOpen]);

  // en complément de defaultCompanyFilter quand on crée à partir de la page "receptionList"
  const { filter: sidebarCompanyFilter } = useSelector(appState);

  const defaultValues = {
    id: null,
    company: sidebarCompanyFilter?._id,
    //  || defaultCompanyFilter,
    orderDate: toLocalDateString(new Date()),
    provider: null,
    warehouse: null,
    reference: null,
    status: OrderStatus.TO_DELIVERED,
    suborders: [],
    providerType: isTransfer ? "transfer" : "provider",
  };

  const computeDefaultValues = orderNewValue
    ? {
        ...defaultValues,
        reference: orderNewValue,
        company: sidebarCompanyFilter,
        //  || defaultCompanyFilter,
      }
    : defaultValues;

  const {
    control,
    getValues,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
    clearErrors,
    trigger,
  } = useForm({
    mode: "onChange",
    defaultValues: order || computeDefaultValues,
  });

  const currentCompany = watch(
    "company",
    (order || computeDefaultValues)?.company
  );

  const { data: warehouses, isLoading } = useQuery(
    [CacheInvalidationAction.GET_ENTREPOTS, currentCompany],
    () => EntrepotService.getEntrepots({ company: currentCompany }),
    {
      enabled: !!currentCompany,
      staleTime: 1000 * 60,
    }
  ) as any;

  const fecthSupplierCompanies = useCallback(async () => {
    const filters = {
      companyTypes: CompanyType.SUPPLIER,
    };
    let companies;
    companies = await queryClient.getQueryData([
      CacheInvalidationAction.GET_COMPANIES,
      filters,
    ]);
    if (!companies) {
      companies = await queryClient.fetchQuery(
        [CacheInvalidationAction.GET_COMPANIES, filters],
        () => CompanyServices.getCompanies(filters),
        { staleTime: 60 * 1000 }
      );
      companies = companies.data;
      await queryClient.setQueryData(
        [CacheInvalidationAction.GET_COMPANIES, filters],
        companies
      );
    }
    setCompaniesSupplier(companies);
  }, [setCompaniesSupplier]);

  useEffect(() => {
    fecthSupplierCompanies();
  }, [fecthSupplierCompanies]);

  // Pre-fetch all dockyards from order suborders to avoid multiple API calls
  // SearchSelect calls queryFn directly (bypassing React Query), so we need to fetch
  // all dockyards at once and then populate individual cache entries
  // Only render SubordersForm after dockyards are cached to avoid race conditions
  useEffect(() => {
    const fetchOrderDockyards = async () => {
      setDockyardsLoaded(false);

      // If no order or no suborders with dockyards, mark as loaded immediately
      if (!order || !order?.suborders?.length) {
        setDockyardsLoaded(true);
        return;
      }

      const hasDockyards = order.suborders.some(
        (suborder: any) => suborder?.dockyard
      );
      if (!hasDockyards) {
        setDockyardsLoaded(true);
        return;
      }

      if (order?.suborders?.length > 0) {
        // Collect all unique dockyard IDs from suborders
        const dockyardIdsSet = new Set<string>();
        order.suborders.forEach((suborder: any) => {
          if (suborder?.dockyard) {
            dockyardIdsSet.add(suborder.dockyard);
          }
        });

        const dockyardIds = Array.from(dockyardIdsSet);

        if (dockyardIds.length > 0) {
          // First, fetch all dockyards in a single call
          const allFiltersInId = {
            $inId: dockyardIds,
          };

          const allQueryKey = [
            CacheInvalidationAction.GET_DOCKYARDS,
            "order-form-all",
            dockyardIds.sort().join(","),
          ];

          let allDockyardsResponse: any = await queryClient.getQueryData(
            allQueryKey
          );
          if (!allDockyardsResponse) {
            allDockyardsResponse = await queryClient.fetchQuery(
              allQueryKey,
              () => DockyardServices.getDockyards(allFiltersInId),
              {
                cacheTime: 5 * 60 * 1000,
                staleTime: 5 * 60 * 1000,
              }
            );
          }

          // Extract dockyards array
          const allDockyards =
            allDockyardsResponse?.data || allDockyardsResponse || [];

          // Then, populate individual cache entries for each dockyard ID
          // This matches what SearchSelect expects when it calls with { $inId: [singleId] }
          // Note: The array must be sorted for consistent cache key matching
          dockyardIds.forEach((dockyardId) => {
            // Ensure the array is sorted for consistent cache key matching
            const sortedIdArray = [dockyardId].sort();
            const singleFiltersInId = {
              $inId: sortedIdArray,
            };

            const singleQueryKey = [
              CacheInvalidationAction.GET_DOCKYARDS,
              singleFiltersInId,
            ];

            // Find the dockyard in the allDockyards array
            const dockyard = Array.isArray(allDockyards)
              ? allDockyards.find((d: any) => (d._id || d.id) === dockyardId)
              : null;

            if (dockyard) {
              // Pre-populate the cache with the single dockyard response
              // This matches the format that SearchSelect expects
              const singleResponse = {
                data: [dockyard],
              };
              queryClient.setQueryData(singleQueryKey, singleResponse);
            }
          });
        }
      }

      // Mark dockyards as loaded (even if there were no dockyards to load)
      setDockyardsLoaded(true);
    };

    fetchOrderDockyards();
  }, [order]);

  // Pre-fetch all products from order suborders to avoid multiple API calls
  // SearchSelect calls queryFn directly (bypassing React Query), so we need to fetch
  // all products at once and then populate individual cache entries
  // Only render SubordersForm after products are cached to avoid race conditions
  useEffect(() => {
    const fetchOrderProducts = async () => {
      setProductsLoaded(false);

      // If no order or no suborders with products, mark as loaded immediately
      if (!order || !order?.suborders?.length) {
        setProductsLoaded(true);
        return;
      }

      // Collect all unique product references from all suborders
      const productReferencesSet = new Set<string>();
      order.suborders.forEach((suborder: any) => {
        suborder?.suborderProducts?.forEach((product: any) => {
          if (product?.reference) {
            productReferencesSet.add(product.reference);
          }
        });
      });

      const productReferences = Array.from(productReferencesSet);

      if (productReferences.length > 0 && currentCompany) {
        const companyId = currentCompany._id || currentCompany;

        // Fetch all products in a single call
        // getProductsByReferencesAndCompany returns an array directly
        // Pass isReferenced: true to match ProductSelect behavior
        const allProducts =
          await ProductService.getProductsByReferencesAndCompany(
            productReferences,
            companyId,
            true // isReferenced: true (matching ProductSelect's isReferenced: true)
          );

        // Ensure we have an array
        const productsArray = Array.isArray(allProducts) ? allProducts : [];

        // Then, populate individual cache entries for each product reference
        // This matches what SearchSelect expects when it calls with { $inreference: [singleReference] }
        // Note: ProductSelect uses filters with company, but SearchSelect's init only uses $inreference
        // So we cache with just $inreference to match the init call
        productReferences.forEach((productReference) => {
          // Ensure the array is sorted for consistent cache key matching
          const sortedRefArray = [productReference].sort();
          const singleFiltersInRef = {
            $inreference: sortedRefArray,
          };

          const singleQueryKey = [
            CacheInvalidationAction.GET_PRODUCTS,
            singleFiltersInRef,
          ];

          // Find the product in the products array
          const product = productsArray.find(
            (p: any) => p?.reference === productReference
          );

          if (product) {
            // Pre-populate the cache with the single product response
            // SearchSelect has a "Special case for products" that handles arrays directly
            // So we store as an array, not { data: [...] }
            const singleResponse = [product];
            queryClient.setQueryData(singleQueryKey, singleResponse);
          }
        });
      }

      // Mark products as loaded (even if there were no products to load)
      setProductsLoaded(true);
    };

    fetchOrderProducts();
  }, [order, currentCompany]);

  const { isContributor, user, isClient } = useAuth();

  const companyId = sidebarCompanyFilter?._id;
  const { data: myContributorWarehouses } = useQuery(
    [
      CacheInvalidationAction.GET_ENTREPOTS,
      isContributor(),
      { company: companyId },
    ],
    () => {
      // We use this only if we have the contributor role
      if (isContributor() && !isAdmin()) {
        return EntrepotService.getEntrepots({ parentCompany: companyId });
      }
      return [];
    },
    { cacheTime: 5 * 1000 * 60 }
  );

  const defaultFilterOrders = useMemo(() => {
    if (isContributor() && !isAdmin()) {
      // @tech @improve
      // We add the warehouses filter with all 'our' warehouses to see linked orders
      if (isClient()) {
        const nextFilters = {
          $or: [
            {
              // CLIENT ROLE !
              company: { $eq: sidebarCompanyFilter?._id },
            },
            {
              // CONTRIBUTOR ROLE : we want to see all order with OUR warehouses ...
              warehouse: {
                $in: myContributorWarehouses?.data?.map(({ _id }) => _id) ?? [
                  // @tech @improve : Force the get with 0 results while myContributorWarehouses list is still fetching...
                  "impossibleId",
                ],
              },
              // ... But only if the order is for a company I'm a contributor of :
              company: {
                $in: [
                  ...(user?.userInfo?.userCompany?.contributorOf || []),
                  sidebarCompanyFilter?._id,
                ],
              },
            },
          ],
        };
        return nextFilters;
      }

      // ONLY CONTRIBUTOR : we want to see all order with OUR warehouses ...
      const nextFilters = {
        warehouse: {
          $in: myContributorWarehouses?.data?.map(({ _id }) => _id) ?? [
            // @tech @improve : Force the get with 0 results while myContributorWarehouses list is still fetching...
            "impossibleId",
          ],
        },
        company: {
          $in: [
            ...(user?.userInfo?.userCompany?.contributorOf || []),
            sidebarCompanyFilter?._id,
          ],
        },
      };
      return nextFilters;
    }
    // Not contributor : go back to the "default" behaviour : Use the company filter (if filled)
    return {
      company: { $eq: sidebarCompanyFilter?._id },
      warehouse: undefined,
    };
  }, [
    isContributor,
    isAdmin,
    sidebarCompanyFilter?._id,
    isClient,
    myContributorWarehouses?.data,
    user?.userInfo?.userCompany?.contributorOf,
  ]);

  // Configure options for the receptions query
  const receptionsOptions = useMemo(
    () => ({
      defaultSort: {
        field: "id",
        direction: "desc" as const,
      },
      defaultPageSize: 20,
      // Prisma API includes all relations by default, no need for populate
      ignoreSideBarFilter: isContributor(),
      // Convert Strapi filters to Prisma query parameters
      defaultFilters: {
        // For Prisma API, we need to pass filters as query parameters
        // The order filter will be handled by the backend logic
        ...(order?.id
          ? {
              // When editing an order, filter receptions that are linked to this order
              // This will be handled by the backend based on the orderId parameter
              orderId: order.id,
            }
          : {
              // When creating a new order, return impossible filter to get empty results
              // Use a non-existent order ID to get empty results
              orderId: -1,
            }),
        // Convert company filter from Strapi format to Prisma format
        ...("company" in defaultFilterOrders &&
          defaultFilterOrders.company &&
          "$eq" in defaultFilterOrders.company && {
            company: defaultFilterOrders.company.$eq,
          }),
        // Convert warehouse filter from Strapi format to Prisma format
        ...("warehouse" in defaultFilterOrders &&
          defaultFilterOrders.warehouse &&
          "$in" in defaultFilterOrders.warehouse && {
            warehouse: defaultFilterOrders.warehouse.$in.join(","),
          }),
        // Convert warehouse filter from Strapi format to Prisma format (single value)
        ...("warehouse" in defaultFilterOrders &&
          defaultFilterOrders.warehouse &&
          "$eq" in defaultFilterOrders.warehouse && {
            warehouse: defaultFilterOrders.warehouse.$eq,
          }),
      },

      service: Services.GATEWAY_API,
      jwt: token,
      queryOptions: {
        refetchOnMount: true,
        refetchOnWindowFocus: true,
      },
    }),
    [isContributor, order?.id, defaultFilterOrders, token]
  );

  // Configure options for the EDBs query
  // const edbOptions = useMemo(
  //   () => ({
  //     defaultSort: {
  //       field: "id",
  //       direction: "desc" as const,
  //     },
  //     defaultPageSize: 1000000,
  //     populate: [
  //       "orderProducts",
  //       "orderProducts.order",
  //       "orderProducts.order.suborders",
  //     ],
  //     service: Services.GATEWAY_API,
  //     cacheKey: "order-form-edbs",
  //     jwt: token,
  //   }),
  //   [token]
  // );

  const { togglePopin: toggleReceptionPopin, open: openReception } =
    useReception();

  // Use the new useList hook for receptions with proper JWT authentication
  const {
    data: receptionsItems,
    isLoading: isReceptionsFetching,
    refetch: fetchReceptionsItems,
  } = useList(
    "api/order/receptions/consolidated", // Use the consolidated endpoint
    receptionsOptions
  );

  // Use the new useList hook for EDBs with proper JWT authentication
  // const { data: edbItems = [] } = useList(
  //   "api/order/edbs/consolidated",
  //   edbOptions
  // );

  const handleEditReceptionClick = useCallback(
    async (elem: any) => {
      setEditedReception(elem);
      toggleReceptionPopin(true);
      fetchReceptionsItems();
    },
    [toggleReceptionPopin, fetchReceptionsItems]
  );

  const onSubmitReception = useCallback(
    async (data: any) => {
      await ReceptionService.updateReceptionItem(data.id, data);
      await queryClient.invalidateQueries([
        CacheInvalidationAction.GET_ORDER,
        order?.id,
      ]);
      await queryClient.invalidateQueries([CacheInvalidationAction.GET_ORDERS]);
      toggleReceptionPopin(false);
      queryClient.invalidateQueries(["api/order/receptions/consolidated"]);
      fetchReceptionsItems();
    },
    [toggleReceptionPopin, fetchReceptionsItems, order?.id]
  );

  useEffect(() => {
    trigger("reference");
  }, [currentCompany, trigger]);

  const isOrderReferenceAlreadyUsed = useCallback(
    async (orderReference: string, excludeOrderReference?: string) => {
      if ((currentCompany?._id ?? currentCompany)?.length) {
        try {
          // If updating and the reference is the same as the current order's reference, allow it
          if (
            excludeOrderReference &&
            orderReference === excludeOrderReference
          ) {
            return false;
          }

          const res = await areOrderRefsAlreadyUsed(
            [orderReference],
            currentCompany?._id ?? currentCompany
          );

          return res;
        } catch (error) {
          console.error(error);
          return false;
        }
      }
      return false;
    },
    [currentCompany]
  );

  useEffect(() => {
    if (!toggleReceptionPopin) {
      setEditedReception(null);
    }
  }, [toggleReceptionPopin, setEditedReception]);

  const normalizeReceptions = (receptions) => {
    const receptionsList = extractData(receptions);

    return receptionsList.map((reception) => {
      // Use parseStrapiFormat to clean the entire reception object from Strapi structure
      const normalizedReception = parseStrapiFormat(reception);

      // Build a set of suborder references that are actually part of this reception
      // (present in productQuantity). This prevents new suborders (added after the
      // reception was created) from being falsely detected as part of the reception
      // by ButtonsList, SubordersForm, and ProductsInSubordersForm.
      const receptionPQ =
        normalizedReception.productQuantity ||
        normalizedReception.productQuantities ||
        [];

      const normalizedOrders = extractData(normalizedReception.orders).map(
        (order: any) => {
          // Collect suborder references from productQuantity for this order
          const pqSuborderRefs = new Set(
            receptionPQ
              .filter((pq: any) => pq.orderReference === order.reference)
              .map((pq: any) => pq.suborderReference)
              .filter(Boolean)
          );

          // If the reception has productQuantity entries, only keep suborders
          // that are referenced in them. Otherwise (shouldn't happen for
          // existing receptions), keep all suborders.
          const filteredSuborders =
            pqSuborderRefs.size > 0
              ? (order.suborders || []).filter((s: any) =>
                  pqSuborderRefs.has(s.reference)
                )
              : order.suborders || [];

          return {
            ...order,
            suborders: filteredSuborders,
          };
        }
      );

      // Create a new normalized reception object
      return {
        ...normalizedReception,
        // Normalize orders within the reception (with filtered suborders)
        orders: normalizedOrders,
        // Normalize files within the reception
        files: extractData(normalizedReception.files),
        // You might need to normalize other nested properties depending on your data structure
        receptionReferences: extractData(
          normalizedReception.receptionReferences
        ),
      };
    });
    return receptionsList;
  };

  const normalizedReceptionsItems = useMemo(() => {
    return normalizeReceptions(receptionsItems);
  }, [receptionsItems]);

  useEffect(() => {
    if (!currentCompany) {
      setValue("provider", null);
      setValue("warehouse", null);
      setValue("warehouseOrigin", null);
      setValue("providerType", isTransfer ? "transfer" : "provider");

      // Clear suborders' dockyard, project, and products
      const currentSuborders = getValues("suborders");
      if (currentSuborders && currentSuborders.length > 0) {
        currentSuborders.forEach((_, index) => {
          setValue(`suborders[${index}].dockyard`, null);
          setValue(`suborders[${index}].project`, null);
          setValue(`suborders[${index}].suborderProducts`, [{ reference: "" }]);
        });
      }

      clearErrors("provider");
      clearErrors("warehouse");
      clearErrors("warehouseOrigin");
      clearErrors("providerType");

      // Clear suborders errors including products
      if (currentSuborders && currentSuborders.length > 0) {
        currentSuborders.forEach((_, index) => {
          clearErrors(`suborders[${index}].dockyard`);
          clearErrors(`suborders[${index}].project`);
          clearErrors(`suborders[${index}].suborderProducts`);
        });
      }
    }
  }, [currentCompany, setValue, clearErrors, isTransfer, getValues]);

  return (
    <div className={`${jclasses.jWrapper} ${commonClasses.OrderFormContainer}`}>
      <form onSubmit={handleSubmit(onSubmit)} className={classes.form}>
        <div className={classes.formTop}>
          <Text type="h2">
            {isReadOnly
              ? "Détails de la commande"
              : order
              ? "Éditer la commande"
              : "Ajouter une commande"}
          </Text>
          {isReadOnly && <Badge color="orange">Lecture seule</Badge>}

          <div style={rightStyle}>
            {order && !isReadOnly && order.status !== OrderStatus.CANCELED && (
              <UIButton
                variant="text"
                onClick={handleDeleteOrder}
                disabled={!canDeleteOrder}
                confirm
                confirmMessage={`Êtes-vous sûr de vouloir supprimer la commande ${order.reference} ?`}
                style={{
                  ...buttonStyle,
                  padding: 0,
                  border: "none",
                  backgroundColor: "transparent",
                  boxShadow: "none",
                }}
                title={
                  !canDeleteOrder
                    ? "Impossible de supprimer : la commande a des réceptions ou est intégrée dans une EDB"
                    : "Supprimer la commande"
                }
                sx={{
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                    boxShadow: "none",
                  },
                  "& svg": {
                    color: canDeleteOrder ? "black" : "#B3BAC5", // Black when enabled, light gray when disabled
                  },
                }}
              >
                <DeleteBin2LineIcon style={{ height: "100%", width: "100%" }} />
              </UIButton>
            )}

            {order &&
              !isReadOnly &&
              (order.status !== "canceled" ? (
                <Button
                  variant="text"
                  onClick={() => onCancelOrder(order)}
                  confirm
                  confirmMessage={`Souhaitez-vous vraiment annuler la commande ${order.reference} ?`}
                  disabled={!canDeleteOrder}
                >
                  Annuler la commande
                </Button>
              ) : (
                <Button disabled>Commande annulée</Button>
              ))}
          </div>
        </div>

        <div className={jclasses.formSectionHead}>
          <Text type="h3">Informations commande</Text>
        </div>

        <div className={classes.fieldsRow}>
          {/* entreprise - select */}
          <div className={classes.fieldsCol}>
            <Controller
              control={control}
              name="company"
              rules={{
                required: "Veuillez remplir ce champ",
              }}
              render={({ field: { onChange, value } }) => (
                <CompanySelect
                  label="Entreprise"
                  placeholder="Sélectionnez une entreprise"
                  value={value}
                  rowsPerPage={99999}
                  onChange={(companyId) => {
                    onChange(companyId);
                  }}
                  disabled={Boolean(
                    order || sidebarCompanyFilter || isReadOnly
                  )}
                  required
                  error={!!errors.company}
                  companyTypes={[CompanyType.CLIENT]}
                  minCharsToTrigerSearch={0}
                  ignoreSideBarCompanyFilter={Boolean(
                    order || sidebarCompanyFilter
                  )}
                  activeCompaniesOnly
                  className={commonClasses.select}
                />
              )}
            />
          </div>
          <div className={classes.fieldsCol}>
            <Controller
              control={control}
              name="orderDate"
              rules={{
                required: "Veuillez remplir ce champ",
              }}
              render={({ field }) => (
                <BataillogDatePicker
                  selectedDate={field.value ? new Date(field.value) : undefined}
                  onDateChange={(d) =>
                    field.onChange(
                      d instanceof Date ? toLocalDateString(d) : null
                    )
                  }
                  label="Date de la commande"
                  error={errors.orderDate as unknown as FieldError}
                  variant="outlined"
                  displayCalendarIcon
                  style={{ width: "100%" }}
                  disabled={order?.status === "delivered_ok" || isReadOnly}
                  isClearable={order?.status !== "delivered_ok" && !isReadOnly}
                />
              )}
            />
          </div>
          <div className={classes.fieldsCol} hidden={isTransfer}>
            <CField
              control={control}
              name="providerType"
              required
              error={errors.providerType}
              label="Type de commande"
              disabled={(order && order?.receptions?.length > 0) || isReadOnly}
            >
              {isTransfer
                ? (props: CFieldInputProps) => (
                    <GenericSelect
                      valueKey="value"
                      options={[{ value: "transfer", label: "Transfert" }]}
                      {...props}
                    />
                  )
                : (props: CFieldInputProps) => (
                    <GenericSelect
                      valueKey="value"
                      options={[
                        { value: "provider", label: "Fournisseur" },
                        { value: "reverse", label: "Reverse" },
                      ]}
                      {...props}
                    />
                  )}
            </CField>
          </div>
          {!isTransfer && (
            <div className={classes.fieldsCol}>
              <Controller
                control={control}
                name="provider"
                rules={{
                  required: "Veuillez remplir ce champ",
                }}
                render={({ field }) => {
                  const supplierOf = currentCompany?._id ?? currentCompany;
                  const isProviderDisabled =
                    !supplierOf ||
                    !isAdmin() ||
                    (order && order?.receptions?.length > 0) ||
                    isReadOnly;

                  return (
                    <>
                      <CompanySelect
                        key={`provider-${currentCompany}`}
                        ignoreSideBarCompanyFilter
                        label="Fournisseurs"
                        companyTypes={[CompanyType.SUPPLIER]}
                        minCharsToTrigerSearch={0}
                        rowsPerPage={99999}
                        companyOptions={companiesSupplier?.filter(
                          (c: Company) => c.supplierOf?.includes(supplierOf)
                        )}
                        supplierId={supplierOf}
                        {...field}
                        disabled={isProviderDisabled}
                        required
                        error={errors.provider as unknown as boolean}
                      />
                      {/* Previously, provider was a string. Display the value "as is". */}
                      {!!field.value && !isAnObjectId(field.value) && (
                        <>
                          <Text type="important">Précédente valeur : </Text>
                          <Text>{field.value}</Text>
                        </>
                      )}
                    </>
                  );
                }}
              />
            </div>
          )}
          {isTransfer && (
            <div className={classes.fieldsCol}>
              <Controller
                control={control}
                name="warehouseOrigin"
                rules={{
                  required: "Veuillez remplir ce champ",
                }}
                render={({ field: { onChange, value } }) =>
                  isLoading ? (
                    <Loader />
                  ) : (
                    <WarehouseSelector
                      key={`warehouse-${
                        currentCompany?._id || currentCompany || "no-company"
                      }`}
                      parentFilter={currentCompany?._id ?? currentCompany}
                      parentFilterRequired
                      onChange={(e, data) => {
                        onChange(data?._id);
                      }}
                      value={(warehouses?.data || []).find((warehouse) => {
                        return value === warehouse._id;
                      })}
                      error={errors.warehouse}
                      disabled={
                        (order && order?.receptions?.length > 0) || isReadOnly
                      }
                      variant="outlined"
                      disableClearable
                      required
                      isTransfer={isTransfer}
                      isCompanyWarehouse={false}
                    />
                  )
                }
              />
            </div>
          )}
        </div>
        <div className={classes.fieldsRow}>
          {/* entrepôt - select - TODO api */}
          <div className={classes.fieldsCol}>
            <Controller
              control={control}
              name="warehouse"
              rules={{
                required: "Veuillez remplir ce champ",
              }}
              render={({ field: { onChange, value } }) =>
                isLoading ? (
                  <Loader />
                ) : (
                  <WarehouseSelector
                    parentFilter={currentCompany?._id ?? currentCompany}
                    enableStockManagementFilter
                    parentFilterRequired
                    onChange={(e, data) => {
                      onChange(data?._id);
                    }}
                    value={(warehouses?.data || []).find((warehouse) => {
                      return value === warehouse._id;
                    })}
                    error={errors.warehouse}
                    disabled={
                      (order && order?.receptions?.length > 0) || isReadOnly
                    }
                    variant="outlined"
                    disableClearable
                    required
                    isTransfer={isTransfer}
                  />
                )
              }
            />
          </div>

          {/* n° commande - libre */}
          <div className={classes.fieldsCol}>
            <Controller
              control={control}
              name="reference"
              rules={{
                required: "Veuillez remplir ce champ",
                validate: async (reference: string) => {
                  // Always validate, but when updating, exclude the current order's reference
                  const excludeReference = order?.reference;
                  const match = await isOrderReferenceAlreadyUsed(
                    reference,
                    excludeReference
                  );
                  return !match;
                },
              }}
              render={({ field }) => (
                <>
                  <TextField
                    label="N° de commande"
                    variant="outlined"
                    style={{ width: "100%" }}
                    {...field}
                    error={errors.reference as unknown as boolean}
                    disabled={
                      (order && order?.receptions?.length > 0) ||
                      isReadOnly ||
                      !currentCompany
                    }
                    autoComplete="off"
                    required
                  />
                  {errors.reference &&
                    (errors.reference.type as unknown as string) ===
                      "validate" && (
                      <Text style={{ fontStyle: "italic", fontSize: 10 }}>
                        Cette référence est déjà prise
                      </Text>
                    )}
                </>
              )}
            />
          </div>

          <div className={classes.fieldsCol}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Controller
                control={control}
                name="deliveryDate"
                render={({ field }) => (
                  <BataillogDatePicker
                    selectedDate={
                      field.value ? new Date(field.value) : undefined
                    }
                    onDateChange={(d) =>
                      field.onChange(
                        d instanceof Date ? toLocalDateString(d) : null
                      )
                    }
                    label="Date de livraison estimée"
                    error={errors.deliveryDate as unknown as FieldError}
                    variant="outlined"
                    disabled={
                      (order && order?.receptions?.length > 0) || isReadOnly
                    }
                    isClearable={
                      !(order && order?.receptions?.length > 0) && !isReadOnly
                    }
                  />
                )}
              />
              <div
                style={{
                  minWidth: 100,
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  height: 40,
                }}
              >
                <JBadge
                  variant={getValues("status")}
                  messages={STATUS_ORDERS}
                />
              </div>
            </div>
          </div>
        </div>

        {order &&
          !isReceptionsFetching &&
          Boolean(normalizedReceptionsItems?.length) && (
            <OrderReceptions
              receptions={normalizedReceptionsItems || []}
              editReceptionClick={handleEditReceptionClick}
              isReadOnly={isReadOnly}
            />
          )}

        <div className={jclasses.formSectionHead}>
          <Text type="h3">Pièces jointes</Text>
        </div>

        <Controller
          control={control}
          name="files"
          render={({ field: { onChange, value } }) => {
            const safeValue = extractData(value);
            return (
              <FileUploader
                value={safeValue || []}
                onChange={onChange}
                isAdmin={isAdmin()}
                isReadOnly={isReadOnly}
                isStrapi
                isSplitted
                mainLabel="Pièce(s) jointe(s) de la commande :"
                secondaryLabel="Pièce(s) jointe(s) des réceptions :"
                secondaryValue={normalizedReceptionsItems || []}
                mainEmptyLabel="Aucune pièce jointe pour la commande"
                secondaryEmptyLabel="Aucune pièce jointe pour cette réception"
              />
            );
          }}
        />

        {dockyardsLoaded && productsLoaded ? (
          <SubordersForm
            {...{
              control,
              register,
              defaultValues,
              getValues,
              setValue,
              errors,
              watch,
              currentCompany,
              order,
              clearErrors,
            }}
            receptions={normalizedReceptionsItems}
            ops={currentOPs}
            editReceptionClick={handleEditReceptionClick}
            isReadOnly={isReadOnly}
          />
        ) : (
          <Loader />
        )}

        {!isReadOnly && (
          <div className={classes.footerForm}>
            <Button color="secondary" onClick={onReset}>
              Annuler
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              Enregistrer
            </Button>
          </div>
        )}
      </form>

      <Popin
        className={commonClasses.ReceptionPopin}
        openPopin={openReception}
        toggleDrawer={toggleReceptionPopin}
      >
        <ReceptionForm
          reception={editedReception}
          onSubmit={onSubmitReception}
          onReset={() => toggleReceptionPopin(false)}
          fromOrderForm
        />
      </Popin>
    </div>
  );
}

export default OrderForm;
